/* Eden — product demo player.
   Playback streams through a plain <audio> element, so a track starts on the
   first click instead of after a full download. The waveform is a separate,
   lazier job: the file is fetched and decoded once, in the background, and the
   bars are redrawn when the peaks land. Until then a neutral placeholder holds
   the space, so the row never looks broken or jumps.
   No audio is fetched and no AudioContext exists before the visitor presses play. */
(() => {
  if (customElements.get('eden-audio')) return;

  const BARS = 96;
  const peaksBySrc = new Map();   // src -> Float32Array, shared across instances
  const palettesByCover = new Map();
  let decoder = null;             // one AudioContext for the whole page
  let playing = null;             // the Track currently sounding

  const ctx = () => (decoder ||= new (window.AudioContext || window.webkitAudioContext)());

  const mix = (a, b, amount) => a.map((value, i) => Math.round(value + (b[i] - value) * amount));
  const rgb = (color) => `rgb(${color.join(', ')})`;
  const luminance = (color) => color.reduce((sum, value, i) => {
    const channel = value / 255;
    const linear = channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    return sum + linear * [0.2126, 0.7152, 0.0722][i];
  }, 0);
  const contrast = (a, b) => {
    const first = luminance(a), second = luminance(b);
    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
  };

  const coverPalette = (pixels) => {
    const buckets = new Map();
    let neutral = [0, 0, 0], total = 0;
    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i + 3] < 160) continue;
      const color = [pixels[i], pixels[i + 1], pixels[i + 2]];
      neutral = neutral.map((value, channel) => value + color[channel]);
      total += 1;
      const max = Math.max(...color), min = Math.min(...color);
      const saturation = max ? (max - min) / max : 0;
      if (max < 35 || min > 235 || saturation < 0.14) continue;
      const key = color.map((channel) => channel >> 5).join('-');
      const bucket = buckets.get(key) || { color: [0, 0, 0], count: 0, score: 0 };
      bucket.color = bucket.color.map((value, channel) => value + color[channel]);
      bucket.count += 1;
      bucket.score += 0.35 + saturation;
      buckets.set(key, bucket);
    }
    if (!total) return null;
    const dominant = [...buckets.values()].sort((a, b) => b.score - a.score)[0];
    const color = dominant
      ? dominant.color.map((value) => Math.round(value / dominant.count))
      : neutral.map((value) => Math.round(value / total));
    const soft = mix(color, [250, 250, 247], 0.94);
    const tint = mix(color, [250, 250, 247], 0.85);
    let accent = color;
    for (let i = 0; i < 24 && (contrast(accent, tint) < 4.5 || contrast(accent, [255, 255, 255]) < 4.5); i += 1) {
      accent = mix(accent, [0, 0, 0], 0.09);
    }
    const onAccent = contrast(color, [255, 255, 255]) >= contrast(color, [0, 0, 0]) ? '#fff' : '#000';
    return { color: rgb(color), accent: rgb(accent), soft: rgb(soft), tint: rgb(tint), muted: rgb(mix(color, soft, 0.62)), 'on-accent': onAccent };
  };

  const paletteFrom = (url) => {
    if (!url) return Promise.resolve(null);
    if (palettesByCover.has(url)) return palettesByCover.get(url);
    const pending = new Promise((resolve) => {
      const image = new Image();
      let settled = false;
      const finish = (palette) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        image.onload = null;
        image.onerror = null;
        resolve(palette);
      };
      const timeout = setTimeout(() => finish(null), 8000);
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = 40;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(image, 0, 0, 40, 40);
          finish(coverPalette(context.getImageData(0, 0, 40, 40).data));
        } catch {
          finish(null);
        }
      };
      image.onerror = () => finish(null);
      image.src = url;
    });
    palettesByCover.set(url, pending);
    return pending;
  };

  const clock = (seconds) => {
    if (!Number.isFinite(seconds)) return '--:--';
    const s = Math.max(0, Math.round(seconds));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  /** Deterministic stand-in bars, so every reload shows the same shape. */
  const placeholder = (src) => {
    let seed = 0;
    for (let i = 0; i < src.length; i += 1) seed = (seed * 31 + src.charCodeAt(i)) >>> 0;
    const out = new Float32Array(BARS);
    for (let i = 0; i < BARS; i += 1) {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      out[i] = 0.18 + (seed % 1000) / 1000 * 0.22;
    }
    return out;
  };

  /** Peak amplitude per bar, taken from the loudest channel at each position. */
  const peaksOf = (buffer) => {
    const out = new Float32Array(BARS);
    const step = Math.floor(buffer.length / BARS) || 1;
    for (let ch = 0; ch < buffer.numberOfChannels; ch += 1) {
      const data = buffer.getChannelData(ch);
      for (let i = 0; i < BARS; i += 1) {
        let peak = 0;
        const start = i * step;
        const end = Math.min(start + step, data.length);
        // Sparse sampling: full resolution buys nothing at 96 bars and costs a lot.
        for (let j = start; j < end; j += 16) {
          const v = data[j] < 0 ? -data[j] : data[j];
          if (v > peak) peak = v;
        }
        if (peak > out[i]) out[i] = peak;
      }
    }
    let max = 0;
    for (let i = 0; i < BARS; i += 1) if (out[i] > max) max = out[i];
    if (max > 0) for (let i = 0; i < BARS; i += 1) out[i] = out[i] / max;
    return out;
  };

  class Track {
    constructor(li, host) {
      this.host = host;
      this.src = li.dataset.src;
      this.button = li.querySelector('.eden-audio__play');
      this.wave = li.querySelector('.eden-audio__wave');
      this.canvas = li.querySelector('.eden-audio__canvas');
      this.time = li.querySelector('.eden-audio__time');
      this.el = li;
      this.audio = null;
      this.peaks = peaksBySrc.get(this.src) || placeholder(this.src);
      this.real = peaksBySrc.has(this.src);
      this.ratio = 0;
      this.frame = 0;
      this.events = new AbortController();

      this.button.addEventListener('click', () => this.toggle(), { signal: this.events.signal });
      this.wave.addEventListener('pointerdown', (e) => this.grab(e), { signal: this.events.signal });
      this.wave.addEventListener('keydown', (e) => this.key(e), { signal: this.events.signal });

      this.resize = new ResizeObserver(() => this.draw());
      this.resize.observe(this.wave);
      this.draw();
    }

    /* ------------------------------------------------------------- drawing */

    draw() {
      const rect = this.wave.getBoundingClientRect();
      if (!rect.width) return;
      const dpr = Math.min(devicePixelRatio || 1, 2);
      const w = Math.round(rect.width), h = Math.round(rect.height);
      if (this.canvas.width !== w * dpr || this.canvas.height !== h * dpr) {
        this.canvas.width = w * dpr; this.canvas.height = h * dpr;
        this.canvas.style.width = `${w}px`; this.canvas.style.height = `${h}px`;
      }
      const g = this.canvas.getContext('2d');
      if (!g) return;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, w, h);

      const style = getComputedStyle(this.el);
      const played = style.getPropertyValue('--eden-audio-played').trim() || '#6665dc';
      const rest = style.getPropertyValue('--eden-audio-rest').trim() || '#c9c9c2';
      const gap = 2;
      const bars = Math.min(BARS, Math.max(1, Math.floor(w / 3)));
      const barW = Math.max(1, (w - gap * (bars - 1)) / bars);
      const mid = h / 2;
      const cut = this.ratio * bars;

      for (let i = 0; i < bars; i += 1) {
        // A floor keeps silent passages visible as a hairline rather than a gap.
        const amp = Math.max(0.06, this.peaks[Math.floor(i * BARS / bars)]) * (this.real ? 1 : 0.75);
        const barH = Math.max(2, amp * (h - 2));
        const x = i * (barW + gap);
        g.fillStyle = i < cut ? played : rest;
        g.globalAlpha = this.real ? 1 : 0.55;
        const r = Math.min(barW / 2, 1.5);
        g.beginPath();
        g.roundRect(x, mid - barH / 2, barW, barH, r);
        g.fill();
      }
      g.globalAlpha = 1;
    }

    /* ------------------------------------------------------------ playback */

    media() {
      if (this.audio) return this.audio;
      const a = new Audio();
      a.preload = 'none';
      a.src = this.src;
      a.addEventListener('loadedmetadata', () => this.tick(), { signal: this.events.signal });
      a.addEventListener('ended', () => { this.ratio = 0; this.stop(); this.draw(); this.tick(); }, { signal: this.events.signal });
      a.addEventListener('error', () => this.fail(), { signal: this.events.signal });
      this.audio = a;
      return a;
    }

    async toggle() {
      if (this.events.signal.aborted) return;
      const a = this.media();
      if (!a.paused) { this.stop(); return; }
      if (playing && playing !== this) playing.stop();
      playing = this;
      this.el.classList.add('is-loading');
      try {
        await a.play();
      } catch (err) {
        if (this.events.signal.aborted || playing !== this) return;
        this.el.classList.remove('is-loading');
        this.fail();
        return;
      }
      if (this.events.signal.aborted || playing !== this) { a.pause(); return; }
      this.el.classList.remove('is-loading');
      this.el.classList.add('is-playing');
      this.button.setAttribute('aria-pressed', 'true');
      this.run();
      this.load();
    }

    stop() {
      this.audio?.pause();
      this.el.classList.remove('is-playing', 'is-loading');
      this.button.setAttribute('aria-pressed', 'false');
      cancelAnimationFrame(this.frame); this.frame = 0;
      if (playing === this) playing = null;
    }

    fail() {
      this.el.classList.add('is-error');
      this.time.textContent = '--:--';
    }

    run() {
      const step = () => {
        const a = this.audio;
        if (!a || a.paused) return;
        if (a.duration) this.ratio = a.currentTime / a.duration;
        this.draw(); this.tick();
        this.frame = requestAnimationFrame(step);
      };
      this.frame = requestAnimationFrame(step);
    }

    tick() {
      const a = this.audio;
      const total = a?.duration;
      this.time.textContent = a && a.currentTime > 0 ? `${clock(a.currentTime)} / ${clock(total)}` : clock(total);
      const pct = Math.round(this.ratio * 100);
      this.wave.setAttribute('aria-valuenow', String(pct));
      this.wave.setAttribute('aria-valuetext', `${pct} %`);
    }

    /* -------------------------------------------------------------- peaks */

    /** Fetch and decode once per URL; failures just leave the placeholder. */
    async load() {
      if (this.real || this.loading) return;
      this.loading = true;
      try {
        const res = await fetch(this.src, { mode: 'cors' });
        if (!res.ok) throw new Error(res.status);
        const buf = await ctx().decodeAudioData(await res.arrayBuffer());
        const peaks = peaksOf(buf);
        peaksBySrc.set(this.src, peaks);
        this.host.refresh(this.src);
      } catch (err) {
        console.warn('[eden-audio] waveform unavailable', err);
      } finally {
        this.loading = false;
      }
    }

    adopt(peaks) { this.peaks = peaks; this.real = true; this.draw(); }

    /* ------------------------------------------------------------ seeking */

    seek(clientX) {
      const r = this.wave.getBoundingClientRect();
      this.ratio = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
      const a = this.media();
      if (a.duration) a.currentTime = this.ratio * a.duration;
      this.draw(); this.tick();
    }

    grab(e) {
      e.preventDefault();
      this.wave.focus();
      this.seek(e.clientX);
      const move = (ev) => this.seek(ev.clientX);
      const up = () => {
        removeEventListener('pointermove', move);
        removeEventListener('pointerup', up);
        removeEventListener('pointercancel', up);
      };
      addEventListener('pointermove', move, { signal: this.events.signal });
      addEventListener('pointerup', up, { once: true, signal: this.events.signal });
      addEventListener('pointercancel', up, { once: true, signal: this.events.signal });
    }

    key(e) {
      const a = this.media();
      const total = a.duration || 0;
      let handled = true;
      if (e.key === 'ArrowRight') a.currentTime = Math.min(total, a.currentTime + 5);
      else if (e.key === 'ArrowLeft') a.currentTime = Math.max(0, a.currentTime - 5);
      else if (e.key === 'Home') a.currentTime = 0;
      else if (e.key === 'End') a.currentTime = total;
      else if (e.key === ' ' || e.key === 'Enter') this.toggle();
      else handled = false;
      if (!handled) return;
      e.preventDefault();
      if (total) { this.ratio = a.currentTime / total; this.draw(); this.tick(); }
    }

    destroy() {
      this.stop();
      this.events.abort();
      this.resize.disconnect();
      if (this.audio) {
        this.audio.removeAttribute('src');
        this.audio.load();
      }
    }
  }

  class EdenAudio extends HTMLElement {
    static observedAttributes = ['data-cover-url'];

    connectedCallback() {
      if (this.tracks) return;
      this.tracks = Array.from(this.querySelectorAll('.eden-audio__track')).map((li) => new Track(li, this));
      this.updatePalette();
    }

    attributeChangedCallback(name, previous, current) {
      if (previous !== current && this.isConnected && this.tracks) this.updatePalette();
    }

    async updatePalette() {
      const version = this.paletteVersion = (this.paletteVersion || 0) + 1;
      const scope = this.closest('featured-product-information, .product-information, product-information') || this;
      const palette = await paletteFrom(this.dataset.coverUrl);
      if (!this.isConnected || version !== this.paletteVersion) return;
      for (const key of ['color', 'accent', 'soft', 'tint', 'muted', 'on-accent']) {
        if (palette) {
          scope.style.setProperty(`--eden-cover-${key}`, palette[key]);
          this.style.setProperty(`--eden-cover-${key}`, palette[key]);
        } else {
          scope.style.removeProperty(`--eden-cover-${key}`);
          this.style.removeProperty(`--eden-cover-${key}`);
        }
      }
      this.dataset.coverPalette = palette ? 'ready' : 'fallback';
      this.tracks?.forEach((track) => track.draw());
    }

    /** One decode serves every row pointing at the same file. */
    refresh(src) {
      const peaks = peaksBySrc.get(src);
      if (peaks) this.tracks?.forEach((t) => { if (t.src === src) t.adopt(peaks); });
    }

    disconnectedCallback() {
      this.paletteVersion = (this.paletteVersion || 0) + 1;
      this.tracks?.forEach((t) => t.destroy());
      this.tracks = null;
    }
  }

  customElements.define('eden-audio', EdenAudio);
})();
